import React from "react";
import { BUBBLE_KINDS } from "./gameUtils";

const F = { fontFamily: "'Fredoka', sans-serif" };

const RAINBOW_BACKGROUND =
  "conic-gradient(from 30deg, #FF6B6B, #FF9E33, #FFD84D, #45CC73, #21B3FF, #408FFF, #9E6BF5, #FF6B6B)";

const Gloss = ({ size }) => (
  <>
    <span
      aria-hidden
      className="absolute rounded-full bg-white/55"
      style={{
        width: size * 0.28,
        height: size * 0.17,
        left: size * 0.17,
        top: size * 0.13,
        transform: "rotate(-30deg)",
        filter: "blur(1px)",
      }}
    />
    <span
      aria-hidden
      className="absolute rounded-full bg-white/35"
      style={{
        width: size * 0.1,
        height: size * 0.1,
        left: size * 0.64,
        top: size * 0.66,
      }}
    />
  </>
);

// A bubble in one of the iOS games. `bubble.hue` = { name, label, base, deep }
// colours the glass; a math bubble carries `problem` = { text, answer } painted
// in white; kind BOMB is the ✗ hazard and kind RAINBOW the screen-clear treat.
const Balloon = ({ bubble, onPop, popInDuration = 0.18, popInFrom = 0.4 }) => {
  const { hue, kind, problem, x, y, size, age, jolt = 0 } = bubble;
  const isBomb = kind === BUBBLE_KINDS.BOMB;
  const isRainbow = kind === BUBBLE_KINDS.RAINBOW;
  const t = Math.min(1, age / popInDuration);
  const popIn = popInFrom + (1 - popInFrom) * (1 - Math.pow(1 - t, 3));

  const shared = {
    left: x + jolt,
    top: y,
    width: size,
    height: size,
    transform: `translate(-50%, -50%) scale(${popIn})`,
    zIndex: isBomb ? 12 : isRainbow ? 11 : 10,
    cursor: "pointer",
    touchAction: "none",
    willChange: "left, top, transform",
  };

  // When the page pops via canvas-level hit testing (onPopAt) instead, onPop is
  // null and the event must bubble up to the canvas untouched.
  const handlePop = (event) => {
    if (!onPop) return;
    event.stopPropagation();
    onPop(bubble.id);
  };

  if (isBomb) {
    return (
      <button
        type="button"
        onPointerDown={handlePop}
        aria-label="Don't tap the X bubble"
        className="absolute rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-white/90"
        style={{
          ...shared,
          background: "radial-gradient(circle at 34% 30%, #FF6B61 0%, #ED3E3E 52%, #DB2929 100%)",
          border: "3px solid rgba(255,255,255,0.85)",
          boxShadow: "0 8px 18px rgba(204,26,26,0.35)",
        }}
      >
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center text-white"
          style={{ ...F, fontSize: Math.round(size * 0.44), fontWeight: 700, lineHeight: 1 }}
        >
          ✕
        </span>
        <Gloss size={size} />
      </button>
    );
  }

  if (isRainbow) {
    return (
      <button
        type="button"
        onPointerDown={handlePop}
        aria-label="Rainbow bubble — tap to clear the screen"
        className="absolute rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-white/90 bubble-rainbow-pulse"
        style={{
          ...shared,
          background: RAINBOW_BACKGROUND,
          border: "3px solid rgba(255,255,255,0.9)",
          boxShadow: "0 8px 22px rgba(122,92,240,0.4)",
        }}
      >
        <span
          aria-hidden
          className="absolute rounded-full"
          style={{
            inset: 0,
            background:
              "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 60%)",
          }}
        />
        <span
          aria-hidden
          className="absolute inset-0 flex flex-col items-center justify-center text-white"
          style={{ ...F, lineHeight: 1, textShadow: "0 2px 3px rgba(0,0,0,0.25)" }}
        >
          <span style={{ fontSize: Math.round(size * 0.24) }}>✦</span>
          {problem === null && bubble.tapHint ? (
            <span style={{ fontSize: Math.round(size * 0.17), fontWeight: 700, letterSpacing: 1 }}>
              TAP!
            </span>
          ) : null}
        </span>
        <Gloss size={size} />
      </button>
    );
  }

  // A coloured glass bubble — the heart of the colour chase, or a math bubble
  // carrying its sum (colour is decoration there; only the sum decides).
  const base = hue?.base || "#8EDCFF";
  const deep = hue?.deep || "#208BCA";
  const fontScale = problem ? (problem.text.length > 4 ? 0.22 : 0.28) : 0;

  return (
    <button
      type="button"
      onPointerDown={handlePop}
      aria-label={problem ? `Bubble ${problem.text}` : `Pop the ${hue?.name || "colored"} bubble`}
      className="absolute rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
      style={{
        ...shared,
        background: `radial-gradient(circle at 32% 28%, #FFFFFF 0%, ${base} 85%)`,
        border: "3px solid rgba(255,255,255,0.94)",
        boxShadow: `0 7px 16px ${deep}59`,
      }}
    >
      {problem && (
        <span
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center leading-none tabular-nums text-white"
          style={{
            ...F,
            fontWeight: 700,
            fontSize: Math.round(size * fontScale),
            textShadow: `0 2px 2px ${deep}BF`,
          }}
        >
          {problem.text}
        </span>
      )}
      <Gloss size={size} />
    </button>
  );
};

export default React.memo(Balloon);
